import { h } from 'preact';
import styled from 'styled-components';
import { FireIcon } from '../../icons/Fire';
import { TIERS } from '../../constants/Tiers';
import { FullFlameIcon } from '../../icons/FullFlame';
import { useState } from 'preact/hooks';
import { FlameListStyled, TierCircleWrapper, BasicTierCircleStyled, TierLevelPointsStyled } from './tier-widget.styles';
import { useUser } from '@authentic/loyalty-pwa/providers';

export enum TierWidgetType {
  BASIC = 'basic',
  FULL = 'full'
}

interface RewardsWidgetProps {
  type: TierWidgetType;
}

export const breakpoints = {
  sm: '600px',
  md: '960px',
  lg: '1280px',
};

const PointsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 280px;
  height: 154px;
  justify-content: center;
  border-bottom: 1px solid #c8c8c8;
  border-top: 1px solid #c8c8c8;

  @media screen and (max-width: 744px) {
    border-bottom: 0px;
    border-top: 0px;
  }
`;

const LeftPointsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 280px;
  height: 154px;
  justify-content: center;
  border-bottom: 1px solid #c8c8c8;
  border-top: 1px solid #c8c8c8;

  @media screen and (max-width: 744px) {
    border-bottom: 0px;
    border-top: 0px;
    width: 230px;
  }

  @media screen and (max-width: ${breakpoints.sm}) {
    width: 50%;
  }
`;

const PointsInformation = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const TierInformation = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 0 20px;

  @media screen and (max-width: 744px) {
    padding: 0 45px;
  }

  @media screen and (max-width: ${breakpoints.sm}) {
    padding: 0 12px;
  }
`;

const WidgetWrapper = styled.div`
  display: flex;
  align-items: center;

  @media screen and (max-width: 744px) {
    flex-wrap: wrap-reverse;
    justify-content: center;
  }
`;

const Title = styled.label`
  font-family: Nunito Sans;
  font-style: normal;
  font-weight: 800;
  font-size: 16px;
  text-align: center;
  letter-spacing: 0.03em;
  font-size: 16px;
`;

const CurrentPoints = styled.label<{ fontSize: number }>`
  font-family: Bowlby One;
  font-style: normal;
  font-weight: normal;
  font-size: ${p => p.fontSize}px;
  line-height: 40px;
  text-align: center;
  letter-spacing: 0.02em;
  height: 46px;

  &:before {
    content: '';
    display: inline-block;
    width: 10px;
    height: 10px;
    vertical-align: 25%;
    -moz-border-radius: 7.5px;
    -webkit-border-radius: 7.5px;
    border-radius: 7.5px;
    background-color: black;
    margin-right: 5px;
  }

  &:after {
    content: '';
    display: inline-block;
    width: 10px;
    vertical-align: 25%;
    height: 10px;
    -moz-border-radius: 7.5px;
    -webkit-border-radius: 7.5px;
    border-radius: 7.5px;
    background-color: black;
    margin-left: 5px;
  }
`;

const LightLabel = styled.label`
  font-family: Nunito Sans;
  font-style: normal;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  letter-spacing: 0.01em;
`;

const LabelBold = styled.label`
  font-family: Nunito Sans;
  font-style: normal;
  font-weight: 900;
  font-size: 12px;
  line-height: 24px;
  text-align: center;
  letter-spacing: 0.02em;
  color: #000000;
`;

const RightPanel = styled.div`
  display: flex;
  flex-direction: row;
  height: 154px;

  @media screen and (max-width: 744px) {
    border-bottom: 1px solid #c8c8c8;
    border-top: 1px solid #c8c8c8;
    justify-content: center;
    width: 464px;
  }

  @media screen and (max-width: ${breakpoints.sm}) {
    width: 100%;
  }
`;

const LeftPanel = styled.div`
  height: 154px;
`;

const FireLogoWrapper = styled.div`
  margin-top: 13px;
  display: flex;
  width: 100px;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const TierCircleStyled = styled.div<{ color: string }>`
  width: 172px;
  height: 172px;
  border: 7px solid ${p => p.color};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex-shrink: 0;
  margin-top: -9px;
  background-color: #E5E5E5;
  span {
    font-size: 16px;
    text-transform: uppercase;
    font-weight: 400;
    font-family: 'Bowlby One';
    color: ${p => p.color};
  }
`;

const ListTiers = TIERS;
let userTierName = '';
let userCurrentBalance: number = -1;
let userEarnXPoints: number = -1;
let userLevelPts: number = -1;
let userNextTier: string = '';
let userExpirationDate = '';
let userTotalPointsInPeriod: number = 0;
export const RewardsPointsWidget = ({
  type
}: RewardsWidgetProps) => {
  const [userTierColor, setUserTierColor] = useState('black');
  const user = useUser();

  const maxTierName = 'epic';
  const defaultTierColor = '#D1D1D1';

  // if params are empty (it's mean user have updated his browser) set default values from user data state
  if (user && user.tier_data) {
    userTierName = user.tier_data.current_tier_name;
    userCurrentBalance = user.tier_data.current_balance;
    userEarnXPoints = user.tier_data.earn_x_points;
    userLevelPts = user.summary?.current_tier_data.metric_thresholds.tier_points || 0;
    userNextTier = user.tier_data.next_tier || '';
    if (user.profile) {
      userTotalPointsInPeriod = user.profile.total_points_in_period;
      userExpirationDate = user.profile.tier_period_end_date;
    }
  }

  const currentUserTier = TIERS.find((t) => t.id.toUpperCase() == userTierName.toUpperCase());
  if (currentUserTier) {
    setUserTierColor(currentUserTier.color);
  }

  const fireIconColor = (color: string, tierIndex: number) => {
    if (currentUserTier) {
      if (tierIndex <= currentUserTier.index) {
        return color;
      }
    }
    return defaultTierColor;
  };

  const parseDate = (expirationDate: string) => {
    const dateItems = expirationDate.split('-');
    return `${dateItems[1]}/${dateItems[2]}/${dateItems[0]}`;
  };

  return (

    <div>
      {type === TierWidgetType.FULL && (
        <WidgetWrapper>
          <LeftPanel>
            <PointsWrapper>
              <PointsInformation>
                <Title>YOU HAVE</Title>
                <CurrentPoints fontSize={userCurrentBalance > 99999 ? 30 : 37}>{userCurrentBalance} PTS</CurrentPoints>
                <LightLabel>{(userCurrentBalance >= 250) ? 'Redeem for eligible rewards below' : `Earn ${250 - userCurrentBalance} points to redeem for eligible rewards`} </LightLabel>
              </PointsInformation>
            </PointsWrapper>
          </LeftPanel>

          <RightPanel>
            <TierCircleStyled color={userTierColor}>
              <FullFlameIcon color={userTierColor}></FullFlameIcon>
              <span>{userTierName.toUpperCase()}</span>
            </TierCircleStyled>
            <LeftPointsWrapper>
              <TierInformation>
                <LightLabel>
                  {(userTierName === maxTierName || !userTierName)
                    ? `Continue to earn points and retain your status through ${parseDate(userExpirationDate)}.`
                    : `Earn ${userEarnXPoints} more points before ${parseDate(userExpirationDate)} and unlock ${userNextTier} level`}
                </LightLabel>

                <FireLogoWrapper>
                  {ListTiers.map((item) => (
                    <FireIcon width={19} height={22.73} color={fireIconColor(item.color, item.index)} />
                  ))}
                </FireLogoWrapper>

                <LabelBold>
                  {(userTierName === maxTierName) ? `${userLevelPts}+` : `${userTotalPointsInPeriod}/${userLevelPts}`} LEVEL PTS
                </LabelBold>
              </TierInformation>
            </LeftPointsWrapper>
          </RightPanel>
        </WidgetWrapper>
      )}

      {type === TierWidgetType.BASIC && (
        <TierLevelPointsStyled>
          <TierCircleWrapper>
            <BasicTierCircleStyled color={userTierColor}>
              <FullFlameIcon color={userTierColor}></FullFlameIcon>
              <span>{userTierName.toUpperCase()}</span>
            </BasicTierCircleStyled>
          </TierCircleWrapper>

          <p>{(userTierName === maxTierName)
            ? `Continue to earn points and retain your status through ${parseDate(userExpirationDate)}.`
            : `Earn ${userEarnXPoints} more points before ${parseDate(userExpirationDate)} and unlock ${userNextTier} level`}</p>

          <FlameListStyled>
            {ListTiers.map((item) => (
              <FireIcon width={19} height={22.73} color={fireIconColor(item.color, item.index)} />
            ))}
          </FlameListStyled>

          <span>{(userTierName === maxTierName) ? `${userLevelPts}+` : `${userTotalPointsInPeriod}/${userLevelPts}`} LEVEL PTS</span>
        </TierLevelPointsStyled>
      )}
    </div>
  );
};
